import { useState } from "react";

interface Order {
  id: string;
  productId: string;
  productTitle: string;
  productImage: string;
  buyer: string;
  buyerEmail: string;
  buyerPhone: string;
  quantity: number;
  total: number;
  status: string;
  trackingNumber: string;
  carrier: string;
  orderDate: string;
  address: string;
  referredBy: string;
  referralPlatform: string;
}

interface OrderCardProps {
  order: Order;
  onUpdateTracking: (orderId: string, tracking: string, carrier: string) => void;
}

const carriers = ["UPS", "USPS", "FedEx", "DHL", "Amazon Logistics", "OnTrac"];

const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-700", icon: "ri-time-line" },
  processing: { label: "Processing", color: "bg-orange-100 text-orange-700", icon: "ri-loader-4-line" },
  shipped: { label: "Shipped", color: "bg-green-100 text-green-700", icon: "ri-truck-line" },
  delivered: { label: "Delivered", color: "bg-gray-100 text-gray-600", icon: "ri-checkbox-circle-line" },
};

const platformIcons: Record<string, string> = {
  tiktok: "ri-tiktok-line",
  youtube: "ri-youtube-line",
  twitter: "ri-twitter-x-line",
  instagram: "ri-instagram-line",
};

export default function OrderCard({ order, onUpdateTracking }: OrderCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [trackingInput, setTrackingInput] = useState(order.trackingNumber);
  const [carrierInput, setCarrierInput] = useState(order.carrier || "UPS");
  const [notifyMethod, setNotifyMethod] = useState<"email" | "phone">("email");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const status = statusConfig[order.status] || statusConfig.pending;

  const handleSendTracking = () => {
    if (!trackingInput) return;
    setSending(true);
    setTimeout(() => {
      onUpdateTracking(order.id, trackingInput, carrierInput);
      setSending(false);
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    }, 1000);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-orange-200 transition-colors">
      {/* Header */}
      <div className="flex items-center gap-4 p-4">
        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
          <img src={order.productImage} alt={order.productTitle} className="w-full h-full object-cover object-top" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs text-gray-400 font-mono">{order.id}</p>
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">{order.productTitle}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                <i className="ri-user-line mr-1" />{order.buyer} · Qty: {order.quantity}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${status.color}`}>
                <i className={status.icon} />{status.label}
              </span>
              <span className="text-sm font-bold text-gray-900">${order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Referral badge */}
      {order.referredBy && (
        <div className="mx-4 mb-3 flex items-center gap-2 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${platformIcons[order.referralPlatform] || "ri-star-line"} text-rose-500 text-sm`} />
          </div>
          <span className="text-xs text-rose-700">Referred by <strong>{order.referredBy}</strong></span>
          <span className="ml-auto text-xs text-rose-500 font-medium">+Commission</span>
        </div>
      )}

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 border-t border-gray-50 text-xs text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <span>{expanded ? "Hide details" : "View details & send tracking"}</span>
        <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base`} />
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-50 pt-4 space-y-4">
          {/* Buyer Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Buyer Contact</p>
              <p className="text-sm font-medium text-gray-800">{order.buyer}</p>
              <p className="text-xs text-gray-500">{order.buyerEmail}</p>
              <p className="text-xs text-gray-500">{order.buyerPhone}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Shipping Address</p>
              <p className="text-xs text-gray-700 leading-relaxed">{order.address}</p>
              <p className="text-xs text-gray-400 mt-1">Ordered: {order.orderDate}</p>
            </div>
          </div>

          {/* Tracking Section */}
          <div className="border border-dashed border-orange-200 rounded-xl p-4 bg-orange-50/30">
            <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <i className="ri-truck-line text-orange-500" />
              Send Tracking Number
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Carrier</label>
                <select
                  value={carrierInput}
                  onChange={(e) => setCarrierInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none focus:border-orange-400 cursor-pointer"
                >
                  {carriers.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tracking Number</label>
                <input
                  type="text"
                  value={trackingInput}
                  onChange={(e) => setTrackingInput(e.target.value)}
                  placeholder="Enter tracking number"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs text-gray-500">Notify via:</span>
              <div className="flex gap-2">
                {(["email", "phone"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setNotifyMethod(m)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${notifyMethod === m ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                  >
                    <i className={m === "email" ? "ri-mail-line" : "ri-phone-line"} />
                    {m === "email" ? order.buyerEmail : order.buyerPhone}
                  </button>
                ))}
              </div>
            </div>

            {sent && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3 text-xs text-green-700">
                <i className="ri-checkbox-circle-fill text-green-500" />
                Tracking number sent to {notifyMethod === "email" ? order.buyerEmail : order.buyerPhone}!
              </div>
            )}

            <button
              onClick={handleSendTracking}
              disabled={!trackingInput || sending}
              className="w-full py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {sending ? (
                <span><i className="ri-loader-4-line animate-spin mr-2" />Sending...</span>
              ) : (
                <span><i className="ri-send-plane-line mr-2" />Send Tracking to Customer</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
